import { useRouter } from "next/router";
import React from "react";
import { useIntl } from "react-intl";

import {
  BookingFormSubmitProps,
  useBookingFormSubmit,
} from "shared/hooks/bookingFormSubmit";

import BookingPriceConfirmation from "@components/Flights/BookingPriceConfirmation";
import TermsAndConditions from "@components/Flights/TermsAndConditions";
import FormattedMessage from "@components/UI/FormattedMessage";
import useModalAlert from "@components/UI/useModalAlert";

import { addPaymentInfo } from "@lib/dataLayers";
import {
  errorCodesToRedirect,
  routeChangeFallback,
  scrollToErrorDynamicForm,
} from "@lib/webFunctions";

export const REDIRECT_TIMEOUT_MS = 500;

export default function BookingFormSubmit({
  refs,
  fullPrice,
}: BookingFormSubmitProps) {
  const router = useRouter();
  const intl = useIntl();

  const [ModalAlert, showError] = useModalAlert();

  const onSubmitError = async () => {
    await scrollToErrorDynamicForm();
  };

  const redirectToBooked = async ({
    reservationsIds,
    email,
    currency,
    paymentAdditionalPrice,
  }) => {
    try {
      const fullPriceWithPaymentPrice = `${
        Number(fullPrice) + paymentAdditionalPrice
      }`;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "d4.result.bookSuccess",
        fullPrice: fullPriceWithPaymentPrice,
        currency,
        reservationId: reservationsIds,
        eventCallback: async () => {
          await router.push({
            pathname: "/booked",
            query: { reservationsIds, email },
          });
        },
      });

      setTimeout(async () => {
        await router.push({
          pathname: "/booked",
          query: { reservationsIds, email },
        });
      }, REDIRECT_TIMEOUT_MS);

      // eslint-disable-next-line no-empty
    } catch (e) {}
  };

  const showCustomError = ({
    messageId,
    messageText,
    onClose,
  }: {
    messageId?: string;
    messageText?: string;
    onClose?: () => void;
  }) => {
    showError(
      messageId ? intl.formatMessage({ id: messageId }) : messageText,
      onClose
    );
  };

  const navigateToPayment = async (paymentUrl) => {
    await new Promise((resolve) => setTimeout(resolve, 0));
    window.location.href = paymentUrl;
  };

  const onCustomErrorClose = (errorCode: string) => {
    if (!errorCodesToRedirect.includes(errorCode)) {
      return;
    }

    routeChangeFallback(() => {
      router.push("/");
    });
    router.back();
  };

  const {
    onSubmit,
    isConditionsConfirmed,
    isConditionsError,
    toggleIsConditionsConfirmed,
    unconfirmedReservations,
    summaryPrices,
    alternativeCurrencySummaryPrices,
    currency,
    alternativeCurrency,
    onUnconfirmedReservationSubmit,
    isFetchingServices,
    isSendingBookingForm,
  } = useBookingFormSubmit({
    refs,
    fullPrice,
    onSubmitError,
    redirectToBooked,
    showCustomError,
    navigateToPayment,
    isHotel: false,
    onCustomErrorClose,
    fireGtmEvents: (bookingData) => addPaymentInfo(bookingData, intl, currency),
    alternativeCurrency: router.query?.currency,
  });

  const disableButton =
    isSendingBookingForm === true || isFetchingServices === true;

  return (
    <div className="container">
      <div className="result-box result-confirmation">
        <TermsAndConditions
          getRefsForFlightOffer={() => refs}
          error={isConditionsError}
          confirmed={isConditionsConfirmed}
          toggleConditions={toggleIsConditionsConfirmed}
        />

        <br className="only-mobile" />

        <button
          disabled={disableButton}
          className="button-result-finish button-secondary-color"
          onClick={async () => await onSubmit()}
          type="button"
        >
          <FormattedMessage id="Result.finish" />
        </button>
      </div>

      {unconfirmedReservations !== null && (
        <BookingPriceConfirmation
          show
          originalPrice={
            alternativeCurrencySummaryPrices?.OriginalTotalPrice ||
            summaryPrices?.OriginalTotalPrice
          }
          newPrice={
            alternativeCurrencySummaryPrices?.ActualTotalPrice ||
            summaryPrices?.ActualTotalPrice
          }
          currency={alternativeCurrency || currency}
          onConfirm={onUnconfirmedReservationSubmit}
        />
      )}

      {/* @ts-ignore */}
      <ModalAlert />
    </div>
  );
}
